import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import BlogRecommendations from '../../../components/feature/BlogRecommendations';
import BlogRelatedStrip from '../../../components/feature/BlogRelatedStrip';
import BlogProgressBar from '../../../components/feature/BlogProgressBar';
import BlogTableOfContents from '../../../components/feature/BlogTableOfContents';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

export default function TermLifeInsuranceIndianapolisBlog() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Term Life Insurance Indianapolis: Complete 2025 Guide | Hoosier Life';
    
    const setMeta = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (property.startsWith('og:') || property.startsWith('twitter:')) {
          meta.setAttribute('property', property);
        } else {
          meta.setAttribute('name', property);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    setMeta('description', 'Complete guide to term life insurance in Indianapolis. Costs, coverage amounts, top carriers & how to get the best rates for Indiana families in 2025.');
    setMeta('keywords', 'term life insurance Indianapolis, Indianapolis life insurance rates, term life Indiana, affordable life insurance Indianapolis');
    setMeta('last-modified', new Date().toISOString());
    setMeta('og:title', 'Term Life Insurance Indianapolis: Complete 2025 Guide');
    setMeta('og:description', 'Complete guide to term life insurance in Indianapolis. Learn about costs, coverage amounts, top carriers, and how to get the best rates for Indiana families.');
    setMeta('og:url', `${SITE_URL}/blog/term-life-insurance-indianapolis`);
    setMeta('og:type', 'article');
    setMeta('og:image', 'https://public.readdy.ai/ai/img_res/02dbe09dc66daeb7f3bc35dd548e6e30.jpg');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:image', 'https://readdy.ai/api/search-image?query=happy%20Indianapolis%20family%20of%20four%20smiling%20in%20bright%20home%20protected%20by%20term%20life%20insurance%20warm%20natural%20sunlight%20parents%20with%20children%20safe%20secure%20family%20clean%20background&width=1200&height=630&seq=og-term-life-indy&orientation=landscape');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/blog/term-life-insurance-indianapolis`);

    // Inject Schema.org JSON-LD
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Term Life Insurance Indianapolis: Complete 2025 Guide',
        description: 'Everything Indianapolis families need to know about term life insurance in 2025. Compare rates, coverage options, and find the best policy for your family.',
        author: {
          '@type': 'Person',
          '@id': 'https://hoosierlifeinsurance.com/about#agent',
          name: 'Benjamin Wright',
          jobTitle: 'Licensed Life Insurance Agent',
          url: 'https://hoosierlifeinsurance.com/about',
          worksFor: { '@type': 'InsuranceAgency', name: 'Hoosier Life Insurance', url: SITE_URL },
          knowsAbout: ['Term Life Insurance', 'Life Insurance Indianapolis', 'Indiana Life Insurance Rates'],
        },
        publisher: {
          '@type': 'Organization',
          name: 'Hoosier Life Insurance',
          url: SITE_URL
        },
        datePublished: '2025-01-15',
        dateModified: '2025-03-23T00:00:00Z',
        url: `${SITE_URL}/blog/term-life-insurance-indianapolis`,
        mainEntityOfPage: `${SITE_URL}/blog/term-life-insurance-indianapolis`,
        image: { '@type': 'ImageObject', url: 'https://readdy.ai/api/search-image?query=happy%20Indianapolis%20family%20of%20four%20smiling%20in%20bright%20home%20protected%20by%20term%20life%20insurance%20warm%20natural%20sunlight%20parents%20with%20children%20safe%20secure%20family%20clean%20background&width=1200&height=630&seq=og-term-life-indy&orientation=landscape', width: 1200, height: 630 },
        timeRequired: 'PT12M',
        wordCount: 2400,
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          url: SITE_URL
        },
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', 'h2', 'details summary']
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Hoosier Life Insurance',
        url: SITE_URL,
        logo: `${SITE_URL}/logo.png`,
        description: 'Independent life insurance agency serving Indianapolis and all of Indiana. Licensed specialists helping families find affordable coverage from 20+ top-rated carriers.',
        areaServed: {
          '@type': 'State',
          name: 'Indiana'
        },
        knowsAbout: ['Term Life Insurance', 'Whole Life Insurance', 'Final Expense Insurance', 'Life Insurance Indianapolis', 'Indiana Life Insurance']
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How much does term life insurance cost in Indianapolis?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'A healthy 35-year-old in Indianapolis pays $32/month (male) or $27/month (female) for $500,000 of 20-year term coverage. Indianapolis residents pay 8–12% below the national average due to Indiana\'s competitive insurance market.'
            }
          },
          {
            '@type': 'Question',
            name: 'What term length should I choose for life insurance in Indianapolis?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Choose a term that matches your financial obligations. 20-year terms are most popular for young Indianapolis families with mortgages and young children. 30-year terms work best if you have a new baby or just bought a home. 10-year terms suit those with minimal debt or near retirement.'
            }
          },
          {
            '@type': 'Question',
            name: 'Can I get term life insurance without a medical exam in Indianapolis?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. Most Indianapolis residents under age 60 qualify for no-exam term life insurance up to $500,000–$1,000,000 through accelerated underwriting. Carriers use prescription databases and medical records to approve applications within 24–48 hours with no blood draw required.'
            }
          }
        ]
      }
    ];

    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.id = `schema-blog-term-life-${index}`;
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      schemas.forEach((_, index) => {
        const script = document.getElementById(`schema-blog-term-life-${index}`);
        if (script) script.remove();
      });
    };
  }, []);

  return (
    <>
      <title>Term Life Insurance Indianapolis: 2026 Complete Guide | Hoosier Life</title>
      <meta name="description" content="Everything Indianapolis families need to know about term life insurance in 2026: real costs by age, coverage amounts, top carrier rankings, and how to lock in the best rate." />
      <link rel="canonical" href={`${SITE_URL}/blog/term-life-insurance-indianapolis`} />
      <meta name="robots" content="index, follow" />
      <div className="min-h-screen bg-white">
        <BlogProgressBar />
        <Header />

        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="bg-[#1A3A52] border-b border-[#1A3A52]/20">
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
            <Link to="/" className="flex items-center gap-1.5 text-white/70 hover:text-[#D97642] transition-colors">
              <i className="ri-home-4-line text-base"></i>
              <span>Home</span>
            </Link>
            <i className="ri-arrow-right-s-line text-white/40"></i>
            <Link to="/blog" className="text-white/70 hover:text-[#D97642] transition-colors">Blog</Link>
            <i className="ri-arrow-right-s-line text-white/40"></i>
            <span className="text-[#D97642] font-semibold">Term Life Insurance Indianapolis</span>
          </div>
        </nav>

        <article className="max-w-4xl mx-auto px-6 py-16">
          {/* Hero */}
          <header className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Term Life Insurance Indianapolis: Complete 2026 Buyer's Guide
            </h1>
            <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-8">
              <span className="flex items-center gap-2">
                <i className="ri-calendar-line text-[#D97642]"></i>
                Updated March 2025
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-time-line text-[#D97642]"></i>
                12 min read
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-map-pin-line text-[#D97642]"></i>
                Indianapolis, IN
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-shield-check-line text-[#D97642]"></i>
                Licensed Indiana Specialists
              </span>
            </div>
            <p className="text-xl text-gray-700 leading-relaxed">
              Everything Indianapolis families need to know about term life insurance in 2025: costs, coverage amounts, top carriers, and how to get the best rates for your family.
            </p>
          </header>

          {/* Key Takeaways — AI extraction box */}
          <div className="bg-[#1A3A52] text-white rounded-2xl p-7 mb-12">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <i className="ri-bookmark-3-fill text-[#D97642] text-xl"></i>
              Key Takeaways
            </h2>
            <ul className="space-y-2.5">
              {[
                'Term life covers a fixed period (10, 20, or 30 years) at a locked monthly premium — the most affordable life insurance for Indianapolis families',
                'A healthy 35-year-old in Indianapolis pays $32/month (male) or $27/month (female) for $500,000 of 20-year term coverage',
                'Term life is 8–10× cheaper than whole life insurance, making it the right choice for 90% of Indiana families with mortgages and children',
                'Most Indianapolis families need 10–12× their annual income plus outstanding debts',
                'Indiana rates run 8–12% below the national average — and you can get approved in 24–48 hours without a medical exam',
              ].map((point, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-white/90">
                  <i className="ri-check-line text-[#D97642] mt-0.5 flex-shrink-0"></i>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {/* Table of Contents */}
          <BlogTableOfContents
            title="In This Guide"
            items={[
              { id: 'what-is-term-life', label: 'What Is Term Life Insurance?' },
              { id: 'term-life-costs', label: 'How Much Does Term Life Cost in Indianapolis?' },
              { id: 'coverage-amounts', label: 'How Much Coverage Do Indianapolis Families Need?' },
              { id: 'top-carriers', label: 'Top Term Life Carriers in Indianapolis' },
              { id: 'term-faq', label: 'Frequently Asked Questions' },
            ]}
          />

          {/* Main Content */}
          <div className="prose prose-lg max-w-none">
            <h2 id="what-is-term-life" className="text-3xl font-bold text-gray-900 mb-4 mt-12">What Is Term Life Insurance?</h2>
            {/* Answer Capsule */}
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Term life insurance pays a tax-free lump sum to your family if you die within the policy period. It does not build cash value — which is why it costs a fraction of permanent life insurance. For most Indianapolis families protecting a mortgage and raising children, term is the right choice.
            </div>
            <p className="text-gray-700 leading-relaxed mb-6">
              Term life insurance provides coverage for a specific period (typically 10, 20, or 30 years) at a fixed monthly premium. If you pass away during the term, your beneficiaries receive a tax-free death benefit. If you outlive the term, the policy expires with no payout.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              For Indianapolis families, term life is the most affordable way to protect against mortgage debt, income loss, and future expenses like college tuition. A healthy 35-year-old can get $500,000 in coverage for as little as $25-$40/month.
            </p>

            <div className="bg-[#FDF6F0] border-l-4 border-[#D97642] p-6 my-8 rounded-r-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <i className="ri-lightbulb-line text-[#D97642] mr-3 text-2xl"></i>
                Quick Answer
              </h3>
              <p className="text-gray-800 leading-relaxed">
                <strong>Term life insurance is ideal for 90% of Indianapolis families.</strong> It's 8-10x cheaper than whole life, provides high coverage amounts during your working years, and frees up money for retirement savings and home improvements.
              </p>
            </div>

            <h2 id="term-life-costs" className="text-3xl font-bold text-gray-900 mb-4 mt-12">How Much Does Term Life Insurance Cost in Indianapolis?</h2>
            {/* Answer Capsule */}
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Indianapolis residents pay $23–$115/month for $500,000 of 20-year term coverage depending on age and gender. Indiana rates are 8–12% below the national average. A healthy 35-year-old male pays $32/month; a 35-year-old female pays $27/month.
            </div>
            <p className="text-gray-700 leading-relaxed mb-6">
              Indianapolis residents benefit from lower-than-average life insurance rates due to Indiana's competitive insurance market and lower cost of living. See our full <Link to="/blog/life-insurance-rates-indianapolis" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">Indianapolis life insurance rates guide</Link> for a detailed breakdown by age, health class, and coverage amount.
            </p>

            <div className="overflow-x-auto my-8">
              <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    <th className="px-6 py-4 text-left font-bold">Age</th>
                    <th className="px-6 py-4 text-left font-bold">Male (Non-Smoker)</th>
                    <th className="px-6 py-4 text-left font-bold">Female (Non-Smoker)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 30</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$28/month</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$23/month</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 35</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$32/month</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$27/month</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 40</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$45/month</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$38/month</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 45</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$68/month</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$56/month</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 50</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$115/month</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$92/month</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-sm text-gray-600 italic mb-6">
              *Rates based on healthy non-smokers with Preferred Plus underwriting. Actual rates may vary based on health, lifestyle, and carrier.
            </p>

            <h2 id="coverage-amounts" className="text-3xl font-bold text-gray-900 mb-4 mt-12">How Much Coverage Do Indianapolis Families Need?</h2>
            {/* Answer Capsule */}
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Most Indianapolis families need 10–12× their annual income plus outstanding debts.
            </div>
            <p className="text-gray-700 leading-relaxed mb-6">
              A common rule of thumb is <strong>10-12x your annual income</strong> plus outstanding debts. Use our <Link to="/blog/how-much-life-insurance-indiana" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">Indiana coverage calculator</Link> to find your exact number based on income, mortgage, and family needs. Here's a practical example for an Indianapolis family:
            </p>

            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 my-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Example: The Martinez Family (Fishers, IN)</h3>
              <ul className="space-y-3 text-gray-700 mb-6">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#D97642] mr-3 mt-1"></i>
                  <span><strong>Annual Income:</strong> $85,000</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#D97642] mr-3 mt-1"></i>
                  <span><strong>Mortgage Balance:</strong> $250,000</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#D97642] mr-3 mt-1"></i>
                  <span><strong>Other Debts:</strong> $30,000 (car loans, credit cards)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#D97642] mr-3 mt-1"></i>
                  <span><strong>College Fund Goal:</strong> $100,000 (2 kids)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#D97642] mr-3 mt-1"></i>
                  <span><strong>Final Expenses:</strong> $20,000</span>
                </li>
              </ul>
              <div className="bg-[#1A3A52] text-white rounded-xl p-6">
                <p className="text-lg font-bold mb-2">Recommended Coverage: $1,000,000</p>
                <p className="text-[#D97642] text-sm">
                  This ensures the mortgage is paid off, debts are cleared, kids can attend college, and the surviving spouse has 5-7 years of income replacement.
                </p>
              </div>
            </div>

            <h2 id="top-carriers" className="text-3xl font-bold text-gray-900 mb-4 mt-12">Top Term Life Insurance Carriers in Indianapolis</h2>
            {/* Answer Capsule */}
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> The best term life carriers for Indianapolis families in 2025 are Mutual of Omaha (no-exam up to $1M), Prudential (high coverage amounts), Nationwide (best for health conditions), and Banner Life (lowest rates for healthy applicants). An independent agent shops all four simultaneously at no extra cost to you.
            </div>
            <p className="text-gray-700 leading-relaxed mb-6">
              As an independent agency, we work with 20+ A-rated carriers to find you the best rates. Here are the top performers for Indianapolis families:
            </p>

            <div className="grid md:grid-cols-2 gap-6 my-8">
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Mutual of Omaha</h3>
                <p className="text-sm text-gray-600 mb-4">Best for: No-exam coverage up to $1M</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                    <span>Approval in 24-48 hours</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                    <span>Competitive rates for ages 30-50</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                    <span>Conversion option to whole life</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Prudential</h3>
                <p className="text-sm text-gray-600 mb-4">Best for: High coverage amounts ($2M+)</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                    <span>Excellent rates for healthy applicants</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                    <span>Strong financial ratings (A+)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                    <span>Living benefits riders available</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Nationwide</h3>
                <p className="text-sm text-gray-600 mb-4">Best for: Families with health issues</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                    <span>Lenient underwriting for diabetes, high BP</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                    <span>Flexible term lengths (10-40 years)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                    <span>Return of premium option</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Banner Life</h3>
                <p className="text-sm text-gray-600 mb-4">Best for: Budget-conscious families</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                    <span>Lowest rates for healthy applicants</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                    <span>No-exam up to $500K</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                    <span>Fast online application</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Related Blog Links */}
            <div className="bg-gray-50 rounded-2xl p-7 my-10">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-links-line text-[#D97642]"></i>
                Related Reading
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Link to="/blog/how-much-life-insurance-indiana" className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-200 hover:border-[#D97642]/40 transition-colors group">
                  <div className="w-10 h-10 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-calculator-line text-[#1A3A52]"></i>
                  </div>
                  <div>
                    <span className="font-bold text-gray-900 text-sm group-hover:text-[#D97642] transition-colors block">How Much Life Insurance Do I Need?</span>
                    <p className="text-xs text-gray-500 mt-1">Calculate your exact coverage amount with Indiana-specific data</p>
                  </div>
                </Link>
                <Link to="/blog/mortgage-protection-indianapolis" className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-200 hover:border-[#D97642]/40 transition-colors group">
                  <div className="w-10 h-10 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-home-4-line text-[#1A3A52]"></i>
                  </div>
                  <div>
                    <span className="font-bold text-gray-900 text-sm group-hover:text-[#D97642] transition-colors block">Mortgage Protection Insurance</span>
                    <p className="text-xs text-gray-500 mt-1">How term life protects your home and family</p>
                  </div>
                </Link>
              </div>
            </div>

            <h2 id="term-faq" className="text-3xl font-bold text-gray-900 mb-4 mt-12">Frequently Asked Questions</h2>
            
            <div className="space-y-6 my-8">
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">How much does term life insurance cost in Indianapolis?</h3>
                <p className="text-gray-700 leading-relaxed">
                  A healthy 35-year-old in Indianapolis can get $500,000 of 20-year term coverage for $25-$40/month. Rates vary based on age, health, term length, and coverage amount. Indianapolis residents typically pay 8-12% less than the national average.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">What term length should I choose?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Choose a term length that matches your financial obligations. 20-year terms are most popular for young families with mortgages. 30-year terms work well if you have young children. Not sure whether term or permanent coverage fits best? Read our <Link to="/blog/term-vs-whole-life-indianapolis" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">term vs. whole life comparison guide</Link> for a side-by-side breakdown.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Can I get term life insurance without a medical exam in Indianapolis?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Yes. Most Indianapolis residents qualify for <Link to="/blog/no-medical-exam-life-insurance-indiana" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">no-exam term life insurance</Link> up to $500,000-$1,000,000 through accelerated underwriting.
                </p>
              </div>
            </div>

            {/* Serving Your City */}
            <div className="bg-[#F9F7F4] rounded-2xl p-7 my-10">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Term Life Insurance Near You</h2>
              <p className="text-sm text-gray-600 mb-5">We help Indiana families across the state find affordable coverage. Find local rate guidance for your city:</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { to: '/carmel-life-insurance', city: 'Carmel', desc: 'High-income families & CNO Financial professionals' },
                  { to: '/fishers-life-insurance', city: 'Fishers', desc: 'Young families & Roche / Navient employees' },
                  { to: '/noblesville-life-insurance', city: 'Noblesville', desc: 'Hamilton County homeowners & growing families' },
                  { to: '/life-insurance-fort-wayne', city: 'Fort Wayne', desc: 'Manufacturing workers & Sweetwater employees' },
                  { to: '/life-insurance-south-bend', city: 'South Bend', desc: 'Notre Dame staff & AM General workers' },
                ].map(({ to, city, desc }) => (
                  <Link
                    key={to}
                    to={to}
                    className="flex items-center gap-3 bg-white rounded-xl p-4 border border-gray-100 hover:border-[#D97642]/40 transition-colors group cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-[#D97642]/10 rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-2-line text-[#D97642] text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-bold text-gray-900 text-sm group-hover:text-[#D97642] transition-colors">{city}</span>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{desc}</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-[#D97642] transition-colors flex-shrink-0"></i>
                  </Link>
                ))}
              </div>
            </div>

            {/* Author Bio */}
            <div className="border border-gray-200 rounded-2xl p-7 my-10 flex items-start gap-5">
              <div className="w-14 h-14 flex items-center justify-center bg-[#1A3A52] rounded-full flex-shrink-0">
                <i className="ri-shield-user-line text-white text-2xl"></i>
              </div>
              <div>
                <p className="font-bold text-gray-900 text-base mb-1">Written by the Licensed Life Insurance Specialists at Hoosier Life Insurance</p>
                <p className="text-sm text-gray-600 leading-relaxed mb-2">
                  Our team of licensed Indiana life insurance agents has helped thousands of Indianapolis families find the right coverage at the right price. We are independent agents — not tied to any single carrier — so we shop 20+ A-rated companies to find your best rate. All information is reviewed for accuracy and updated regularly to reflect current Indiana market conditions.
                </p>
                <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Licensed in Indiana</span>
                  <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Independent — 20+ Carriers</span>
                  <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Last reviewed March 2025</span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white rounded-2xl p-8 my-12">
              <h2 className="text-3xl font-bold mb-4">Ready to Protect Your Indianapolis Family?</h2>
              <p className="text-xl text-white/80 mb-6">
                Get your free quote in 5 minutes and see how affordable term life insurance can be.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/instant-quote" 
                  className="px-8 py-4 bg-white text-[#1A3A52] rounded-lg font-bold hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  Get Instant Quote
                </Link>
                <Link 
                  to="/book-appointment" 
                  className="px-8 py-4 bg-[#0F2A3F] text-white rounded-lg font-bold hover:bg-[#0A1F2E] transition-colors border-2 border-white/30 whitespace-nowrap"
                >
                  Talk to an Agent
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Mid-Article Quiz CTA */}
        <section className="py-12 bg-gradient-to-br from-orange-50 to-amber-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
              <i className="ri-question-line text-3xl text-orange-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Not Sure Which Policy Is Right for You?
            </h3>
            <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
              Take our 2-minute quiz to get a personalized recommendation based on your family's unique needs and budget.
            </p>
            <a
              href="/quiz"
              className="inline-flex items-center gap-2 bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-700 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Take the Quiz Now
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </section>

        {/* You May Also Like Widget */}
        <BlogRecommendations topic="term-life" />

        <BlogRelatedStrip currentSlug="term-life-insurance-indianapolis" />

        <Footer />
      </div>
    </>
  );
}